import {useCallback, useEffect, useRef, useState} from 'react';
import useAgentSessionStore from '../store/agentSessionStore';
import {getSession} from '../services/agentSessionService';

export default function useActiveSessionLoader() {
  const activeSessionId = useAgentSessionStore((s) => s.activeSessionId);
  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;

  const [sessionDetail, setSessionDetail] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [sessionLoadError, setSessionLoadError] = useState(null);

  const loadSession = useCallback(async (requestedId) => {
    if (!requestedId) {
      setSessionDetail(null);
      setIsLoadingSession(false);
      setSessionLoadError(null);
      return;
    }

    setIsLoadingSession(true);
    setSessionLoadError(null);

    try {
      const session = await getSession(requestedId);

      if (activeSessionIdRef.current !== requestedId) {
        return;
      }

      setSessionDetail(session);
    } catch (err) {
      if (activeSessionIdRef.current === requestedId) {
        console.error('Error loading session:', err);
        setSessionLoadError(err);
        setSessionDetail(null);
      }
    } finally {
      if (activeSessionIdRef.current === requestedId) {
        setIsLoadingSession(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!activeSessionId) {
      setSessionDetail(null);
      setIsLoadingSession(false);
      setSessionLoadError(null);
      return undefined;
    }

    let cancelled = false;
    const requestedId = activeSessionId;

    loadSession(requestedId).then(() => {
      if (cancelled) {
        return;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeSessionId, loadSession]);

  // Listen for local campaign state updates and update sessionDetail.status
  useEffect(() => {
    const handler = (e) => {
      const data = e?.detail;
      if (!data) return;
      const eventSessionId = data?.agentSessionId;
      if (!eventSessionId) return;

      if (activeSessionIdRef.current && activeSessionIdRef.current === eventSessionId) {
        setSessionDetail((prev) => ({...(prev || {}), status: data.campaignStatus ?? prev?.status}));
      }
    };

    window.addEventListener('campaign_state_update_local', handler);
    return () => window.removeEventListener('campaign_state_update_local', handler);
  }, []);

  const refetch = useCallback(() => {
    if (activeSessionIdRef.current) {
      return loadSession(activeSessionIdRef.current);
    }
    return Promise.resolve();
  }, [loadSession]);

  return {
    sessionDetail,
    isLoadingSession,
    sessionLoadError,
    refetch,
  };
}
